import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { twoSvApi } from '../../services/api';
import useEmailedLinkToken from './useEmailedLinkToken';
import '../auth/LoginDesign.css';

// WebUI #222 — pages behind the links Core emails:
//   /account/2sv/recovery?token=       (recoveryAssertion.service.ts)
//   /account/2sv/undo-disable?token=   (twoSv.service.ts undoDisableTwoSv)
//   /auth/report-compromise            (newDeviceLoginNotification.service.ts)
// A GET only renders a confirm screen. The token is POSTed to Core only from an
// explicit button click, because mail scanners and link previewers issue GETs.
//
// Core wire shapes (twoSv.routes.ts, errorHandler.ts):
//   success  { success: true, data: {...} }
//   failure  { success: false, error: CODE, message }   e.g. 410 RECOVERY_TOKEN_INVALID,
//            403 RECOVERY_USER_SUSPENDED, 410 TWO_SV_UNDO_TOKEN_INVALID

export const SUPPORT_EMAIL = 'support@studioaeye.com';

const errorCodeOf = (err) => {
  const data = err?.response?.data || {};
  if (typeof data.error === 'string') return data.error;
  return data.code || data.error?.code || '';
};

const describeFailure = (err, kind) => {
  const code = errorCodeOf(err);
  const status = err?.response?.status;
  if (code === 'RECOVERY_TOKEN_INVALID' || code === 'TWO_SV_UNDO_TOKEN_INVALID' || status === 410) {
    return kind === 'undo'
      ? 'This link is invalid, has expired or has already been used. Nothing was changed.'
      : 'This recovery link is invalid, has expired or has already been used. Nothing was changed.';
  }
  if (code === 'RECOVERY_USER_SUSPENDED') {
    return 'This account is suspended, so 2-step verification recovery cannot be used. Nothing was changed.';
  }
  if (!err?.response) {
    return 'We could not reach StudioAEye, so we do not know whether this was applied. Check your connection and try again.';
  }
  return 'StudioAEye could not complete this request. Nothing is confirmed as changed; try again or contact support.';
};

const Shell = ({ kicker, title, subtitle, children, testId }) => (
  <div className="login-page verify-email-page" data-testid={testId}>
    <header className="login-header">
      <div className="login-header-inner">
        <Link to="/" className="login-logo">
          Studio<span className="login-logo-accent">AEye</span>
        </Link>
        <Link to="/login" className="login-back-link">Back to login</Link>
      </div>
    </header>
    <main className="login-main">
      <section className="login-card verify-email-card" aria-live="polite">
        <div className="login-heading">
          <p className="verify-email-kicker">{kicker}</p>
          <h1 className="login-title">{title}</h1>
          {subtitle ? <p className="login-subtitle">{subtitle}</p> : null}
        </div>
        {children}
        <p className="login-footer">© StudioAEye {new Date().getFullYear()}</p>
      </section>
    </main>
  </div>
);

const MissingToken = ({ kicker, testId }) => (
  <Shell
    kicker={kicker}
    title="This link is incomplete"
    subtitle="The link is missing its token, so there is nothing to confirm. Open the link from the original email again."
    testId={testId}
  >
    <div role="alert" className="login-alert login-alert-error" data-testid="security-link-missing-token">
      Nothing was changed.
    </div>
    <div className="verify-email-actions">
      <Link to="/login" className="login-link verify-email-secondary-link">Back to login</Link>
    </div>
  </Shell>
);

export const TwoSvRecoveryPage = () => {
  const token = useEmailedLinkToken();
  // phase: confirm | working | recovered | aborted | error
  const [phase, setPhase] = useState('confirm');
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState('');

  const inFlight = useRef(false);

  const run = async (action) => {
    // A ref, not `phase`: two clicks in one tick both see the stale state.
    if (inFlight.current) return;
    inFlight.current = true;
    setPhase('working');
    try {
      if (action === 'redeem') {
        const response = await twoSvApi.redeemRecovery(token);
        setDetail(response?.data?.data || {});
        setPhase('recovered');
      } else {
        await twoSvApi.abortRecovery(token);
        setPhase('aborted');
      }
    } catch (err) {
      setMessage(describeFailure(err, 'recovery'));
      setPhase('error');
      inFlight.current = false;
    }
  };

  if (!token) return <MissingToken kicker="2-step verification recovery" testId="two-sv-recovery-page" />;

  if (phase === 'recovered') {
    return (
      <Shell kicker="2-step verification recovery" title="Recovery complete" testId="two-sv-recovery-page">
        <div className="login-alert login-alert-success" data-testid="two-sv-recovery-done">
          Your 2-step verification methods and backup codes were removed and your sessions were signed out
          {typeof detail?.revokedSessionCount === 'number' ? ` (${detail.revokedSessionCount} signed out)` : ''}.
          Sign in, then set 2-step verification up again.
        </div>
        <div className="verify-email-actions">
          <Link to="/login" className="login-submit verify-email-primary-link">Sign in</Link>
        </div>
      </Shell>
    );
  }

  if (phase === 'aborted') {
    return (
      <Shell kicker="2-step verification recovery" title="Recovery cancelled" testId="two-sv-recovery-page">
        <div className="login-alert login-alert-success" data-testid="two-sv-recovery-aborted">
          This recovery was cancelled. Your 2-step verification was not changed. If you did not expect this email,
          change your password and contact {SUPPORT_EMAIL}.
        </div>
        <div className="verify-email-actions">
          <Link to="/login" className="login-link verify-email-secondary-link">Back to login</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      kicker="2-step verification recovery"
      title="Confirm 2-step verification recovery"
      subtitle="An administrator started a recovery for your account. Confirming removes all your 2-step verification methods and backup codes and signs you out everywhere."
      testId="two-sv-recovery-page"
    >
      {phase === 'error' ? (
        <div role="alert" className="login-alert login-alert-error" data-testid="two-sv-recovery-error">{message}</div>
      ) : null}
      <div className="verify-email-actions">
        <button
          type="button"
          className="login-submit verify-email-primary-link"
          data-testid="two-sv-recovery-confirm"
          disabled={phase === 'working'}
          onClick={() => run('redeem')}
        >
          {phase === 'working' ? 'Working...' : 'Confirm recovery'}
        </button>
        <button
          type="button"
          className="login-link verify-email-secondary-link"
          data-testid="two-sv-recovery-abort"
          disabled={phase === 'working'}
          onClick={() => run('abort')}
        >
          This was not me — cancel the recovery
        </button>
      </div>
    </Shell>
  );
};

export const TwoSvUndoDisablePage = () => {
  const token = useEmailedLinkToken();
  // phase: confirm | working | restored | error
  const [phase, setPhase] = useState('confirm');
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState('');

  const inFlight = useRef(false);

  const confirm = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPhase('working');
    try {
      const response = await twoSvApi.undoDisable(token);
      setDetail(response?.data?.data || {});
      setPhase('restored');
    } catch (err) {
      setMessage(describeFailure(err, 'undo'));
      setPhase('error');
      inFlight.current = false;
    }
  };

  if (!token) return <MissingToken kicker="2-step verification" testId="two-sv-undo-page" />;

  if (phase === 'restored') {
    return (
      <Shell kicker="2-step verification" title="2-step verification restored" testId="two-sv-undo-page">
        <div className="login-alert login-alert-success" data-testid="two-sv-undo-done">
          Your 2-step verification is back on
          {typeof detail?.restoredMethodCount === 'number' ? ` (${detail.restoredMethodCount} method(s) restored)` : ''}
          {' '}and your sessions were signed out. Sign in again. If you did not turn it off, change your password.
        </div>
        <div className="verify-email-actions">
          <Link to="/login" className="login-submit verify-email-primary-link">Sign in</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      kicker="2-step verification"
      title="Undo turning off 2-step verification"
      subtitle="Confirming turns your 2-step verification back on and signs you out everywhere. Nothing happens until you confirm."
      testId="two-sv-undo-page"
    >
      {phase === 'error' ? (
        <div role="alert" className="login-alert login-alert-error" data-testid="two-sv-undo-error">{message}</div>
      ) : null}
      <div className="verify-email-actions">
        <button
          type="button"
          className="login-submit verify-email-primary-link"
          data-testid="two-sv-undo-confirm"
          disabled={phase === 'working'}
          onClick={confirm}
        >
          {phase === 'working' ? 'Working...' : 'Turn 2-step verification back on'}
        </button>
        <Link to="/login" className="login-link verify-email-secondary-link">Leave it off</Link>
      </div>
    </Shell>
  );
};

// Core has no report-compromise route yet (Core #244: newDeviceLoginNotification
// emails /auth/report-compromise but auth.routes.ts defines none). This page
// therefore sends NOTHING and says so; it must not claim a report was received.
export const ReportCompromisePage = () => {
  useEmailedLinkToken();
  return (
    <Shell
      kicker="Account security"
      title="Report a suspicious sign-in"
      subtitle="Online reporting is not available yet, so this page has not sent or recorded anything."
      testId="report-compromise-page"
    >
      <div className="login-alert login-alert-error" role="alert" data-testid="report-compromise-unavailable">
        If you did not sign in, act now: change your password, then email {SUPPORT_EMAIL} with the time of the sign-in
        email so support can investigate.
      </div>
      <div className="verify-email-actions">
        <Link to="/forgot-password" className="login-submit verify-email-primary-link">Reset my password</Link>
        <a href={`mailto:${SUPPORT_EMAIL}`} className="login-link verify-email-secondary-link">Contact support</a>
      </div>
    </Shell>
  );
};
